import delve from 'dlv'
import csvDownload from 'react-file-download'
import XLSX from 'xlsx'
import { manifest } from '@fielded/forth-utils'
import { getActualProductId } from './../products/id-handler'
import moment from 'moment'
import InvoiceCache from './InvoiceCache'
import {sortByField} from '../helpers/utils'
import {findProduct} from './../products/utils'

const normalise = str => `${str}`.replace(/[,\s+]/g, ' ')

const stopsToCsv = stops => {
  const fields = [
    '_id',
    'jobId',
    'status',
    'createdAt',
    'updatedAt',
    'location.latitude',
    'location.longitude',
    'address.number',
    'address.street',
    'address.apartment',
    'address.state',
    'address.city',
    'address.postalCode',
    'accepted.time',
    'start.time',
    'start.latitude',
    'start.longitude',
    'arrival.time',
    'arrival.latitude',
    'arrival.longitude',
    'completion.time',
    'completion.comment',
    'completion.latitude',
    'completion.longitude',
    'completion.signatureUrl',
    'driver.fullName',
    'driver.phoneNumber',
    'driver.lastOnlineAt'
  ]

  const rows = stops.map(stop => fields
    .map(field => delve(stop, field, ''))
    .map(normalise)
    .join(','))

  return `${fields.join(',')}\n${rows.join('\n')}`
}

export const downloadCsv = jobs => {
  const stops = jobs.reduce((index, job) => index.concat(job.stops), [])
  const csv = stopsToCsv(stops)
  const date = new Date().toISOString().split('T')[0].replace(/-/g, '')
  csvDownload(csv, `forth-jobs-${date}.csv`)
}

export const formatAddress = address => {
  const fields = [
    'number',
    'street',
    'apartment',
    'city',
    'state',
    'postalCode'
  ]
  return fields
    .filter(field => address[field])
    .map(field => address[field])
    .slice(0, 3)
    .join(', ')
}

const getDocsWithXDays = (docs, days = 45) => {
  const now = moment()
  return docs.filter(doc => now.diff(doc.createdAt, 'days') <= days)
}

const isProductSold = (invoices, name) => {
  for (let invoice of invoices) {
    for (let item of invoice.items) {
      if (item.name === name) {
        return true
      }
    }
  }
  return false
}

/**
 * Generate .xlsx sheet
 * @param paginatedSheet Array<{job, dispensed, stockBalance}>
 * @param products
 */
export const downloadStockBalance = async (paginatedSheet, products) => {
  const body = []
  const metrics = [
    {
      title: 'Location/Client',
      fn: ({stopName}) => stopName
    },
    {
      title: 'Country',
      fn: ({ country }) => country
    },
    {
      title: 'SKU',
      fn: ({productId}) => productId
    },
    {
      title: 'Product',
      fn: ({name}) => name
    },
    {
      title: 'Date',
      fn: ({completionDate}) => completionDate
    },
    {
      title: 'Units sold in period',
      fn: ({openingQty, closingQty}) => openingQty - closingQty
    },
    {
      title: 'Opening balance from previous period',
      fn: ({openingQty}) => openingQty
    },
    {
      title: 'Closing balance on delivery',
      fn: ({closingQty}) => closingQty
    },
    {
      title: 'Units given/dispensed',
      fn: ({dispensed, name}) => parseInt(dispensed.products[name], 10) || 0
    },
    {
      title: 'Units prepacked',
      fn: ({qty}) => (parseInt(qty, 10) || 0)
    },
    {
      title: 'Stockout',
      fn: ({openingQty, closingQty}) => (openingQty - closingQty) === openingQty ? 1 : 0
    },
    {
      title: 'Days since last sales(under 45 days)',
      fn: ({isSold}) => isSold ? 1 : 0
    },
    {
      title: 'Price',
      fn: ({price}) => (Number(price) || 0)
    }
  ]

  const headers = metrics.map(({title}) => title)
  let allInvoices = []

  const allStops = paginatedSheet.reduce((allStops, {job: {stops, invoices}}) => {
    allInvoices = allInvoices.concat(invoices)
    return allStops.concat(stops)
  }, [])

  const getProductByName = name => findProduct(products, name, {lookBehind: true})

  for (let stop of allStops) {
    const {address, note, completion} = stop
    const jobBalance = paginatedSheet.find(item => item.job.job._id === stop.jobId) || {dispensed: [], stockBalance: []}
    // use the latest doc for both dispensed  and stockbalance
    jobBalance.dispensed = sortByField(jobBalance.dispensed, 'createdAt', 'DESC')
    jobBalance.stockBalance = sortByField(jobBalance.stockBalance, 'createdAt', 'DESC')

    const dispensed = jobBalance.dispensed.find(item => item.stopId === stop._id) || {products: {}}
    const stockBalance = jobBalance.stockBalance.find(item => item.stopId === stop._id) || {products: {}}
    const skus = manifest.parse(note || '')
    const stopInvoice = allInvoices.find(invoice => invoice.stopId === stop._id)
    let completionDate = completion ? moment(completion.time).format('YYYY-MM-DD') : ''
    if (stopInvoice) {
      completionDate = moment(stopInvoice.createdAt).format('YYYY-MM-DD')
    }

    // all invoices for a particular stop so we can get if any
    // sales was done for a product under 45 days
    const invoices = await InvoiceCache.getInvoicesByStop(address.city)
    const invoiceUnder45 = getDocsWithXDays(invoices)

    for (let sku of skus) {
      const closingQty = parseInt(stockBalance.products[sku.name], 10) || 0
      const openingQty = parseInt(sku.openingQty, 10) || 0
      const isSold = isProductSold(invoiceUnder45, sku.name)
      const product = getProductByName(sku.name) || {}
      const productId = getActualProductId(product._id)
      const param = { ...sku, dispensed, stockBalance, manifest: skus, closingQty, openingQty, isSold, stopName: address.city, productId, completionDate, country: address.country }

      const skuRow = []
      for (let metric of metrics) {
        skuRow.push(metric.fn(param))
      }
      body.push(skuRow)
    }
  }

  const ws = XLSX.utils.aoa_to_sheet([
    headers,
    ...body
  ])
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Sheet')
  XLSX.writeFile(wb, `stockbalance-${new Date().toISOString()}.xlsx`)
}



// WEBPACK FOOTER //
// ./src/jobs/utils.js